import DropdownMenu from '@pelckmans/elementis/components/dropdown-menu';
import List from '@pelckmans/elementis/components/list';
import Details from '@pelckmans/elementis/icons/details';
import Duplicate from '@pelckmans/elementis/icons/duplicate';
import Edit from '@pelckmans/elementis/icons/edit';
import Trash from '@pelckmans/elementis/icons/trash';
import MaterialPresentationPresentModusIcon from '@pelckmans/elementis/icons/material-presentation-present-modus';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { SlideSet } from '../../../../api/slide-set';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../constants/analyticsEvents';

import IdentityContext from '../../../../contexts/IdentityContext';
import ModuleContext from '../../_contexts/ModuleContext';
import { MaterialIcon } from '../../components/MaterialIcon';
import useSlideSets from '../../hooks/useSlideSets';
import SelectionContext from '../_contexts/SelectionContext';
import ActionMenuButton from './ActionMenuButton';
import MaterialPills from './MaterialPills';
import PinnedMaterialButton from './pinned-material/PinnedMaterialButton';
import MaterialListHeader from './MaterialListHeader';
import useAnalytics from '../../../../hooks/useAnalytics';
import ModalContext from '../../../../contexts/ModalContext';
import ConfirmationModal from '../../../../shared-components/ConfirmationModal';

type SlideSetListProps = {
  title: string;
};

export default function SlideSetList({ title }: SlideSetListProps) {
  const { t } = useTranslation();
  const {
    buildDoceoUrl,
    slideSets,
    captureOpenSlideSetEvents,
    buildCopySlideSetUrl,
    deleteUserSlideSet,
    setSlideSets,
  } = useSlideSets();
  const { identity } = useContext(IdentityContext);
  const { module } = useContext(ModuleContext);
  const { showSelection, selectedSlideSets, addSlideSets, removeSlideSets } = useContext(SelectionContext);
  const { capture } = useAnalytics();
  const { showModal } = useContext(ModalContext);

  if (!module) return null;

  function renderActionArea(
    slideSet: SlideSet,
    slideSetUrl: string,
    slideSeOverviewtUrl: string,
    slideSetEdittUrl: string,
  ) {
    if (!identity.isTeacherAlike) return null;

    function captureEditSlideSetEvents(slideSetId: number) {
      capture({
        objectId: slideSetId.toString(),
        eventType: ANALYTICS_EVENT_TYPES.SLIDE_SET_EDIT_MODE_ACCESSED,
        subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL,
      });
    }

    const handleDelete = async (slideSet: SlideSet) => {
      await deleteUserSlideSet(slideSet);
      setSlideSets(slideSets.filter(currentSlideSet => currentSlideSet.id !== slideSet.id));
      capture({
        objectId: slideSet.id.toString(),
        eventType: ANALYTICS_EVENT_TYPES.SLIDE_SET_DELETED,
        subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL,
      });
    };

    const showDeleteModal = (slideSet: SlideSet) => {
      const BASE = 'PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.DELETE_SLIDE_SET.CONFIRMATION_MODAL';
      return showModal(
        <ConfirmationModal
          id={slideSet.id.toString()}
          headerTitle={t(`${BASE}.HEADER_TITLE`, {
            name: slideSet.name,
          })}
          title={t(`${BASE}.TITLE`)}
          message={t(`${BASE}.BODY`)}
          confirmationText={t(`${BASE}.CONFIRMATION_TEXT`)}
          onConfirm={() => handleDelete(slideSet)}
        />,
      );
    };

    return (
      <>
        <PinnedMaterialButton type="slide-set" materialId={slideSet.id} />
        <ActionMenuButton>
          {handleToggle => (
            <>
              <DropdownMenu.Item
                text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.PRESENT_SLIDE_SET.LABEL')}
                textIcon={<MaterialPresentationPresentModusIcon />}
                value={1}
                role="menuitem"
                href={slideSetUrl}
                target="_blank"
                rel="noopener noreferrer"
                as="link"
              />
              <DropdownMenu.Item
                text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.OVERVIEW_SLIDE_SET.LABEL')}
                textIcon={<Details />}
                value={1}
                role="menuitem"
                href={slideSeOverviewtUrl}
                target="_blank"
                rel="noopener noreferrer"
                as="link"
                showSeparator={true}
              />
              {slideSet.userId && (
                <DropdownMenu.Item
                  text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.EDIT_SLIDE_SET.LABEL')}
                  textIcon={<Edit />}
                  value={1}
                  role="menuitem"
                  href={slideSetEdittUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  as="link"
                  onClick={() => {
                    captureEditSlideSetEvents(slideSet.id);
                    handleToggle();
                  }}
                />
              )}
              <DropdownMenu.Item
                text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.DUPLICATE_SLIDE_SET.LABEL')}
                textIcon={<Duplicate />}
                value={1}
                role="menuitem"
                href={
                  module &&
                  buildCopySlideSetUrl(
                    module.id,
                    slideSet.nodeId,
                    slideSet.id,
                    slideSet.versionId,
                    ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL,
                  )
                }
                target="_blank"
                rel="noopener noreferrer"
                as="link"
                onClick={() => handleToggle()}
              />
              {slideSet.userId && (
                <DropdownMenu.Item
                  prominence="danger"
                  text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.DELETE_SLIDE_SET.LABEL')}
                  textIcon={<Trash />}
                  as="button"
                  value={1}
                  role="menuitem"
                  showSeparator={true}
                  onClick={() => {
                    showDeleteModal(slideSet);
                    handleToggle();
                  }}
                />
              )}
            </>
          )}
        </ActionMenuButton>
      </>
    );
  }

  const renderListItem = (slideSet: SlideSet) => {
    const key = `${slideSet.id}-${slideSet.versionId}`;
    const slideSetUrl = buildDoceoUrl(slideSet);
    const slideSetOverviewUrl = buildDoceoUrl(slideSet, false);
    const slideSetEditUrl = buildDoceoUrl(slideSet, true);
    const actionArea = renderActionArea(slideSet, slideSetUrl, slideSetOverviewUrl, slideSetEditUrl);

    const pillGroupProps = {
      'aria-label': t('PAGES.TOC_LEVEL.MATERIAL_PILLS.A11Y_LABEL'),
      children: <MaterialPills isShared={slideSet.shared} />,
      role: 'group',
    };

    if (showSelection) {
      const isSelected = selectedSlideSets.includes(slideSet.id);
      return (
        <List.Item
          key={key}
          title={slideSet.name}
          actionArea={<PinnedMaterialButton type="slide-set" materialId={slideSet.id} />}
          pillGroupProps={pillGroupProps}
          as="checkbox"
          checked={isSelected}
          onClick={() => (isSelected ? removeSlideSets([slideSet.id]) : addSlideSets([slideSet.id]))}
        />
      );
    }

    return (
      <List.Item
        key={key}
        title={slideSet.name}
        icon={<MaterialIcon materialType="slide-set" isUserSlideSet={Boolean(slideSet.userId)} />}
        actionArea={actionArea}
        pillGroupProps={pillGroupProps}
        as="link"
        href={slideSetUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => captureOpenSlideSetEvents(slideSet.id, 'portaal-material')}
      />
    );
  };

  return (
    <List>
      <MaterialListHeader<number>
        title={title}
        showSelection={showSelection}
        selectedMaterial={selectedSlideSets}
        material={slideSets}
        addMaterial={addSlideSets}
        removeMaterial={removeSlideSets}
      />
      {slideSets.map(slideSet => renderListItem(slideSet))}
    </List>
  );
}
